/**
 * Tabs
 * ======================
 * Generic, data attribute driven tab switcher. Any element marked with
 * `data-tabs` becomes a tab group; buttons inside it carrying `data-tab="id"`
 * reveal the panel carrying the matching `data-tab-panel="id"`.
 */
class Tabs {
  /**
   * @param {HTMLElement} root Element wrapping both the tabs and their panels.
   * @param {Object} [options]
   * @param {string} [options.tabSelector] Selector for the tab buttons.
   * @param {string} [options.panelSelector] Selector for the panels.
   * @param {string} [options.activeClass] Class marking the active tab/panel.
   */
  constructor(
    root,
    {
      tabSelector = "[data-tab]",
      panelSelector = "[data-tab-panel]",
      activeClass = "is-active",
    } = {}
  ) {
    this.root = root;
    this.tabSelector = tabSelector;
    this.panelSelector = panelSelector;
    this.activeClass = activeClass;

    this.tabs = [];
    this.panels = [];

    this._onClick = this._onClick.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
  }

  /**
   * Collect the tabs and panels, bind the click handler and open the tab that
   * is already marked active (falling back to the first one).
   */
  init() {
    if (!this.root) return;

    this.tabs = Array.from(this.root.querySelectorAll(this.tabSelector));
    this.panels = Array.from(this.root.querySelectorAll(this.panelSelector));

    if (!this.tabs.length || !this.panels.length) return;

    this.root.addEventListener("click", this._onClick);
    this.root.addEventListener("keydown", this._onKeydown);

    const initial =
      this.tabs.find((tab) => tab.classList.contains(this.activeClass)) ||
      this.tabs[0];

    this.activate(initial.getAttribute("data-tab"));
  }

  /**
   * Show the panel matching the given id and mark its tab as active.
   * @param {string} id Value shared by `data-tab` and `data-tab-panel`.
   */
  activate(id) {
    if (!id) return;

    this.tabs.forEach((tab) => {
      const isActive = tab.getAttribute("data-tab") === id;
      tab.classList.toggle(this.activeClass, isActive);
      tab.setAttribute("aria-selected", isActive ? "true" : "false");
      // Only the selected tab is in the tab order; arrow keys move between them.
      tab.setAttribute("tabindex", isActive ? "0" : "-1");
    });

    this.panels.forEach((panel) => {
      const isActive = panel.getAttribute("data-tab-panel") === id;
      panel.classList.toggle(this.activeClass, isActive);
      panel.hidden = !isActive;
    });
  }

  /**
   * Move between tabs with the arrow, Home and End keys, per the tabs pattern.
   * @param {KeyboardEvent} event
   */
  _onKeydown(event) {
    const tab = event.target.closest(this.tabSelector);
    if (!tab || !this.root.contains(tab)) return;

    const keys = {
      ArrowRight: 1,
      ArrowLeft: -1,
      ArrowDown: 1,
      ArrowUp: -1,
    };

    let next;
    if (event.key in keys) {
      const from = this.tabs.indexOf(tab);
      next = this.tabs[(from + keys[event.key] + this.tabs.length) % this.tabs.length];
    } else if (event.key === "Home") {
      next = this.tabs[0];
    } else if (event.key === "End") {
      next = this.tabs[this.tabs.length - 1];
    }

    if (!next) return;

    event.preventDefault();
    this.activate(next.getAttribute("data-tab"));
    next.focus();
  }

  /**
   * Delegate clicks on the tab buttons.
   * @param {MouseEvent} event
   */
  _onClick(event) {
    const tab = event.target.closest(this.tabSelector);
    if (!tab || !this.root.contains(tab)) return;

    event.preventDefault();
    this.activate(tab.getAttribute("data-tab"));
  }
}

/**
 * Boot every tab group on the page.
 */
const initTabs = () => {
  document.querySelectorAll("[data-tabs]").forEach((root) => {
    new Tabs(root).init();
  });
};

export default initTabs;
export { Tabs };
